import type { Recurrence } from 'api/recurrence_definitions/endpoints/types';
import { buildScheduleSnapshot, type NodeInstanceScheduleSnapshot } from 'api/recurrence_definitions/operations/buildScheduleSnapshot';
import { EditableRecurrenceDefinition } from 'pages/apps/platform/recurrence_definitions/edit/EditableRecurrenceDefinition';

// A manual ad-hoc submit schedule is a SINGLE recurrence rule (per PM: the
// submit page does not support multiple ad-hoc recurrences — multiple rules
// only exist on a saved recurrence definition). We model that one rule with the
// same `EditableRecurrence` the edit page uses, for UI consistency, held inside
// a throwaway `EditableRecurrenceDefinition` whose name/zone go unused — that
// reuses the model's wire⇄editable mapping and `toSave` without a second code
// path. `manualRule(def)` is the single rule.

/** A manual rule is "complete" enough to schedule: window_open needs a window, every other unit needs an interval. */
export const isCompleteRecurrence = (recurrence: Recurrence): boolean =>
  recurrence.unit === 'window_open' ? recurrence.scheduling_window != null : recurrence.interval != null;

/**
 * Create the editable holder for a manual submit schedule: one rule, at the
 * model's own defaults.
 *
 * The rule starts usable rather than deliberately incomplete: "no recurrence" is
 * a schedule source the operator selects, not a blank field, so a form that
 * offers the ad-hoc rule at all is one where the operator asked for it.
 */
export const createManualDefinition = (): EditableRecurrenceDefinition => new EditableRecurrenceDefinition(null);

/** The single editable rule of a manual submit schedule. */
export const manualRule = (definition: EditableRecurrenceDefinition) => definition.recurrences[0];

/** The manual rule as a wire recurrence, or `[]` when it isn't complete (a one-off). */
export const manualWireRecurrences = (definition: EditableRecurrenceDefinition): Recurrence[] => definition.toSave().recurrences.filter(isCompleteRecurrence);

/** Build the node-instance schedule snapshot for a manual submit, or `null` for a one-off. */
export const buildManualSnapshot = (definition: EditableRecurrenceDefinition): NodeInstanceScheduleSnapshot | null =>
  buildScheduleSnapshot(manualWireRecurrences(definition), definition.resubmitOnError, definition.scheduleNext);
